import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface SavedStyle {
  el: HTMLElement;
  overflow: string;
  overflowX: string;
  overflowY: string;
  height: string;
  maxHeight: string;
}

interface StickyStyle {
  el: HTMLElement;
  position: string;
}

interface DownloadOptions {
  hideTaskList?: boolean;
}

export async function downloadGanttAsPdf(
  container: HTMLElement,
  filename: string = "gantt.pdf",
  options: DownloadOptions = {}
): Promise<void> {
  const savedStyles: SavedStyle[] = [];
  const stickyElements: StickyStyle[] = [];
  const hiddenEls: HTMLElement[] = [];

  const containerOriginal = {
    overflow: container.style.overflow,
    height: container.style.height,
    maxHeight: container.style.maxHeight,
  };

  try {
    if (options.hideTaskList) {
      container
        .querySelectorAll("[data-gantt-tasklist]")
        .forEach((el) => {
          const htmlEl = el as HTMLElement;
          hiddenEls.push(htmlEl);
          htmlEl.style.display = "none";
        });
    }

    const allElements = container.querySelectorAll("*");
    allElements.forEach((rawEl) => {
      const el = rawEl as HTMLElement;
      const computed = getComputedStyle(el);

      if (computed.position === "sticky") {
        stickyElements.push({ el, position: el.style.position });
        el.style.position = "relative";
      }

      const hasOverflowClip =
        computed.overflow === "hidden" ||
        computed.overflow === "auto" ||
        computed.overflowY === "hidden" ||
        computed.overflowY === "auto" ||
        computed.overflowX === "hidden" ||
        computed.overflowX === "auto";

      if (hasOverflowClip) {
        savedStyles.push({
          el,
          overflow: el.style.overflow,
          overflowX: el.style.overflowX,
          overflowY: el.style.overflowY,
          height: el.style.height,
          maxHeight: el.style.maxHeight,
        });

        el.style.overflow = "visible";
        el.style.overflowX = "visible";
        el.style.overflowY = "visible";

        if (el.scrollHeight > el.clientHeight + 2) {
          el.style.height = `${el.scrollHeight}px`;
        }
        el.style.maxHeight = "none";
      }
    });

    container.style.overflow = "visible";
    container.style.height = "auto";
    container.style.maxHeight = "none";

    const verticalScrolls = container.querySelectorAll(
      '[class*="verticalScroll"], [class*="VerticalScroll"]'
    );
    const hScrolls = container.querySelectorAll(
      '[class*="horizontalScroll"], [class*="HorizontalScroll"]'
    );
    [...verticalScrolls, ...hScrolls].forEach((el) => {
      const htmlEl = el as HTMLElement;
      hiddenEls.push(htmlEl);
      htmlEl.style.display = "none";
    });

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    const fullWidth = Math.max(container.scrollWidth, container.offsetWidth);
    const fullHeight = Math.max(container.scrollHeight, container.offsetHeight);

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: fullWidth,
      height: fullHeight,
      windowWidth: fullWidth + 100,
      windowHeight: fullHeight + 100,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
    });

    restoreStyles(container, containerOriginal, savedStyles, stickyElements, hiddenEls);

    const pdf = new jsPDF("l", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 5;
    const usableWidth = pageWidth - 2 * margin;
    const usableHeight = pageHeight - 2 * margin;

    const imgData = canvas.toDataURL("image/jpeg", 0.85);
    const ratio = usableWidth / canvas.width;
    const imgHeight = canvas.height * ratio;

    if (imgHeight <= usableHeight) {
      pdf.addImage(imgData, "JPEG", margin, margin, usableWidth, imgHeight);
    } else {
      const totalPages = Math.ceil(imgHeight / usableHeight);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage("a4", "l");
        const yOffset = margin - page * usableHeight;
        pdf.addImage(imgData, "JPEG", margin, yOffset, usableWidth, imgHeight);
      }
    }

    pdf.save(filename);
  } catch (error) {
    restoreStyles(container, containerOriginal, savedStyles, stickyElements, hiddenEls);
    throw error;
  }
}

function restoreStyles(
  container: HTMLElement,
  containerOriginal: { overflow: string; height: string; maxHeight: string },
  savedStyles: SavedStyle[],
  stickyElements: StickyStyle[],
  hiddenEls: HTMLElement[]
) {
  savedStyles.forEach(
    ({ el, overflow, overflowX, overflowY, height, maxHeight }) => {
      el.style.overflow = overflow;
      el.style.overflowX = overflowX;
      el.style.overflowY = overflowY;
      el.style.height = height;
      el.style.maxHeight = maxHeight;
    }
  );

  stickyElements.forEach(({ el, position }) => {
    el.style.position = position;
  });

  hiddenEls.forEach((el) => {
    el.style.display = "";
  });

  container.style.overflow = containerOriginal.overflow;
  container.style.height = containerOriginal.height;
  container.style.maxHeight = containerOriginal.maxHeight;
}
